// Formatage des visuels : nombres, unités métier, dates. Français (fr-FR) par défaut, celui de
// l'écran ; anglais (`lang: 'en'`) pour les textes dessinés dans les images d'un document anglais
// et dans les exports (PNG, SVG) d'un visuel anglais (contrat v1.11). Fonctions pures, partagées
// par les graphiques ECharts, les tuiles React, le tableau alternatif (accessibilité) et l'export CSV.
//
// Espaces : Intl fr-FR sépare les milliers par une espace fine insécable (U+202F) ;
// l'unité est séparée par une espace insécable (U+00A0), « 12,5 % », « 395 486 FCFA ».
// Anglais (en-GB) : virgule des milliers, point décimal, pourcentage collé (« 1,234.5 », « 12.5% »,
// « 395,486 FCFA ») ; notation compacte et noms des mois en tables fixes (« 1.2m », « 1.2bn »,
// « Apr 2026 ») : ceux d'Intl changent avec la version d'ICU (« K » ou « k », « Sept » ou « Sep »).

const NBSP = ' ';

/** Langue des textes dessinés : « en », sinon « fr » (écran, valeur absente ou inconnue). */
export const vizLang = (lang) => (lang === 'en' ? 'en' : 'fr');
const LOCALES = { fr: 'fr-FR', en: 'en-GB' };

const formatters = new Map();
function nf(options, lang = 'fr') {
  const key = lang === 'fr' ? JSON.stringify(options) : `${lang}:${JSON.stringify(options)}`;
  if (!formatters.has(key)) formatters.set(key, new Intl.NumberFormat(LOCALES[lang], options));
  return formatters.get(key);
}

// Unités reconnues → forme affichée et nombre de décimales par défaut
const UNIT_ALIASES = [
  [/^(fcfa|xof|f\s?cfa|cfa)$/i, { label: 'FCFA', decimals: 0, money: true }],
  [/^(%|pct|pourcent|pourcentage)$/i, { label: '%', decimals: 1 }],
  [/^l\s?\/\s?100\s?km$/i, { label: 'L/100 km', decimals: 1 }],
  [/^l\s?\/\s?h$/i, { label: 'L/h', decimals: 1 }],
  [/^(l|litres?)$/i, { label: 'L', decimals: 1 }],
  [/^(km|kilom[eè]tres?)$/i, { label: 'km', decimals: 0 }],
  [/^(km\s?\/\s?h)$/i, { label: 'km/h', decimals: 0 }],
  [/^(h|heures?)$/i, { label: 'h', decimals: 1 }],
  [/^(j|jours?)$/i, { label: 'j', decimals: 0 }],
];

/** Unité normalisée : {label, decimals, money} (label vide si aucune unité). */
export function unitInfo(unit) {
  const raw = typeof unit === 'string' ? unit.trim() : '';
  if (!raw) return { label: '', decimals: 2, money: false };
  for (const [re, info] of UNIT_ALIASES) if (re.test(raw)) return { money: false, ...info };
  return { label: raw, decimals: 2, money: false };
}

// Unités affichées autrement en anglais (« j », jours → « d ») ; les autres sont les mêmes
// (FCFA, %, L, km, h…), et une unité écrite par le modèle reste telle quelle
const UNIT_LABELS_EN = { j: 'd' };
const shownLabel = (info, lang) => (lang === 'en' ? UNIT_LABELS_EN[info.label] ?? info.label : info.label);

/** Libellé affiché d'une unité dans la langue des textes dessinés : « FCFA », « j » (« d » en anglais). */
export const unitLabel = (unit, { lang } = {}) => shownLabel(unitInfo(unit), vizLang(lang));

// Nombre et unité : espace insécable, sauf le pourcentage anglais, collé (« 12.5% »)
const withLabel = (text, label, lang) => (!label ? text : lang === 'en' && label === '%' ? `${text}%` : `${text}${NBSP}${label}`);

// Notation compacte anglaise (en-GB) : « 12k », « 450k », « 1.2m », « 96.4m », « 1.2bn », une
// décimale au plus ; un arrondi qui atteint 1 000 passe au palier suivant (999 950 → « 1m »)
const EN_COMPACT = [[1e12, 'tn'], [1e9, 'bn'], [1e6, 'm'], [1e3, 'k']];
function compactEn(value, signed = false) {
  const abs = Math.abs(value);
  let at = EN_COMPACT.findIndex(([scale]) => abs >= scale);
  const round = (scale) => Math.round((abs / scale) * 10) / 10;
  let rounded = round(at === -1 ? 1 : EN_COMPACT[at][0]);
  if (rounded >= 1000 && at !== 0) {
    at = at === -1 ? EN_COMPACT.length - 1 : at - 1;
    rounded = round(EN_COMPACT[at][0]);
  }
  const sign = rounded === 0 ? '' : value < 0 ? '-' : signed ? '+' : '';
  return `${sign}${nf({ maximumFractionDigits: 1 }, 'en').format(rounded)}${at === -1 ? '' : EN_COMPACT[at][1]}`;
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/**
 * Valeur complète : « 395 486 FCFA », « 10,4 L/100 km », « 12,5 % », « 1 284 » ; en anglais
 * « 395,486 FCFA », « 10.4 L/100 km », « 12.5% », « 1,284 ».
 * `decimals` force la précision ; sinon au plus celle de l'unité (zéros inutiles retirés).
 */
export function formatValue(value, unit, { decimals, signed = false, lang } = {}) {
  if (!isNum(value)) return '—';
  const l = vizLang(lang);
  const info = unitInfo(unit);
  const max = decimals ?? info.decimals;
  const text = nf({
    maximumFractionDigits: max,
    minimumFractionDigits: decimals ?? 0,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }, l).format(value);
  return withLabel(text, shownLabel(info, l), l);
}

/**
 * Valeur courte pour un axe : « 1,2 M », « 450 k », en anglais « 1.2m », « 450k » (l'unité est
 * portée par le titre d'axe).
 */
export function formatAxis(value, unit, { lang } = {}) {
  if (!isNum(value)) return '';
  const l = vizLang(lang);
  const info = unitInfo(unit);
  const abs = Math.abs(value);
  if (abs >= 10000) {
    return l === 'en' ? compactEn(value) : nf({ notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 1 }).format(value);
  }
  // Graduation décimale (pas de 0,5 choisi par ECharts) : jamais arrondie à l'entier,
  // sinon l'axe affiche deux fois la même valeur (« 12, 12, 11, 11 » pour 11,5 et 10,5) ;
  // sous 1, trois chiffres significatifs (0,0143 / 0,0286 : jamais « 0,01 » deux fois)
  const options = Number.isInteger(value)
    ? { maximumFractionDigits: 0 }
    : abs < 1 ? { maximumSignificantDigits: 3 } : { maximumFractionDigits: 2 };
  const text = nf(options, l).format(value);
  return info.label === '%' ? withLabel(text, '%', l) : text;
}

/**
 * Valeur compacte pour une étiquette posée sur le graphique : « 96,4 M FCFA », « 48 », en
 * anglais « 96.4m FCFA » ; la valeur exacte reste dans l'infobulle et le tableau.
 */
export function formatCompact(value, unit, { signed = false, lang } = {}) {
  if (!isNum(value)) return '—';
  const l = vizLang(lang);
  if (Math.abs(value) < 10000) return formatValue(value, unit, { signed, lang: l });
  const info = unitInfo(unit);
  const text = l === 'en'
    ? compactEn(value, signed)
    : nf({ notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 1, signDisplay: signed ? 'exceptZero' : 'auto' }).format(value);
  return withLabel(text, shownLabel(info, l), l);
}

/** Pourcentage d'une part (déjà calculé, 0-100) : « 42,3 % », en anglais « 42.3% ». */
export function formatPercent(value, decimals = 1, { lang } = {}) {
  if (!isNum(value)) return '—';
  const l = vizLang(lang);
  return withLabel(nf({ maximumFractionDigits: decimals }, l).format(value), '%', l);
}

/** Nombre brut pour le CSV d'Excel en français : virgule décimale, sans séparateur de milliers. */
export function formatCsvNumber(value) {
  if (!isNum(value)) return '';
  return String(value).replace('.', ',');
}

/** Libellé d'unité pour un titre d'axe ou un en-tête : « Coût (FCFA) ». */
export function withUnit(label, unit, { lang } = {}) {
  const shown = shownLabel(unitInfo(unit), vizLang(lang));
  if (!label) return shown;
  return shown ? `${label} (${shown})` : label;
}

// ── Dates ──────────────────────────────────────────────────────────────
const MONTH_RE = /^(\d{4})-(\d{2})$/;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const WEEK_RE = /^(\d{4})-W(\d{1,2})$/i;

const dtf = new Map();
function df(options) {
  const key = JSON.stringify(options);
  if (!dtf.has(key)) dtf.set(key, new Intl.DateTimeFormat('fr-FR', { timeZone: 'UTC', ...options }));
  return dtf.get(key);
}

function parseDateLabel(value) {
  const text = String(value ?? '');
  let m = MONTH_RE.exec(text);
  if (m) return { kind: 'month', date: new Date(Date.UTC(+m[1], +m[2] - 1, 1)) };
  m = DAY_RE.exec(text);
  if (m) return { kind: 'day', date: new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])) };
  m = WEEK_RE.exec(text);
  if (m) return { kind: 'week', year: +m[1], week: +m[2] };
  return null;
}

// Mois anglais en tables fixes (Intl en-GB écrit « Sept » ou « Sep » selon la version d'ICU)
export const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const EN_MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Valeur de l'axe du temps en anglais : « April 2026 » (long), « Apr 26 » (axe), « Apr » (même
// année) ; « 15 April 2026 », « 15 Apr » ; semaine « W36 2026 »
function englishCategory(parsed, { long, sameYear }) {
  if (parsed.kind === 'week') return `W${parsed.week} ${parsed.year}`;
  const month = parsed.date.getUTCMonth();
  const year = parsed.date.getUTCFullYear();
  if (parsed.kind === 'month') {
    if (long) return `${EN_MONTHS[month]} ${year}`;
    return sameYear ? EN_MONTHS_SHORT[month] : `${EN_MONTHS_SHORT[month]} ${String(year).slice(-2)}`;
  }
  const day = parsed.date.getUTCDate();
  return long ? `${day} ${EN_MONTHS[month]} ${year}` : `${day} ${EN_MONTHS_SHORT[month]}`;
}

/**
 * Libellé d'une valeur de l'axe X. `long` : forme complète (infobulle, tableau).
 * « 2026-04 » → « avr. 2026 » (long) / « avr. 26 » (axe) ; « 2026-04-15 » → « 15 avr. 2026 » ;
 * en anglais « April 2026 » / « Apr 26 », « 15 April 2026 ».
 */
export function formatCategory(value, { time = false, long = false, sameYear = false, lang } = {}) {
  if (!time) return String(value ?? '');
  const parsed = parseDateLabel(value);
  if (!parsed) return String(value ?? '');
  if (vizLang(lang) === 'en') return englishCategory(parsed, { long, sameYear });
  if (parsed.kind === 'week') return `S${parsed.week} ${parsed.year}`;
  if (parsed.kind === 'month') {
    if (long) return df({ month: 'long', year: 'numeric' }).format(parsed.date);
    return sameYear
      ? df({ month: 'short' }).format(parsed.date)
      : df({ month: 'short', year: '2-digit' }).format(parsed.date);
  }
  if (long) return df({ day: 'numeric', month: 'long', year: 'numeric' }).format(parsed.date);
  return df({ day: 'numeric', month: 'short' }).format(parsed.date);
}

/** Toutes les dates de l'axe tombent-elles la même année ? (axe plus sobre) */
export function sameYearAxis(values) {
  const years = new Set(values.map((v) => String(v ?? '').slice(0, 4)));
  return years.size === 1;
}

/** Période « 2026-04-01/2026-09-30 » → « du 1 avr. 2026 au 30 sept. 2026 ». */
export function formatPeriod(periode) {
  if (typeof periode !== 'string' || !periode.trim()) return '';
  const [from, to] = periode.split('/');
  const a = parseDateLabel(from);
  const b = parseDateLabel(to);
  const fmt = (p) => (p.kind === 'month'
    ? df({ month: 'short', year: 'numeric' }).format(p.date)
    // « 1er janv. 2026 » : ordinal français du premier jour du mois
    : df({ day: 'numeric', month: 'short', year: 'numeric' }).format(p.date).replace(/^1 /, '1er '));
  if (a?.date && b?.date) return `du ${fmt(a)} au ${fmt(b)}`;
  return periode;
}

/** Bornes affichées d'une période (« 1er janv. 2025 », « 30 sept. 2026 »), ou null. */
export function periodBounds(periode) {
  if (typeof periode !== 'string' || !periode.includes('/')) return null;
  const [from, to] = periode.split('/');
  const a = parseDateLabel(from);
  const b = parseDateLabel(to);
  if (!a?.date || !b?.date || a.kind === 'month' || b.kind === 'month') return null;
  const fmt = (p) => df({ day: 'numeric', month: 'short', year: 'numeric' }).format(p.date).replace(/^1 /, '1er ');
  return [fmt(a), fmt(b)];
}

// ── Textes fixes des graphiques ─────────────────────────────────────────
// Français : les textes historiques de l'écran ; anglais : images d'un document anglais et
// exports d'un visuel anglais. pair : « Objectif : 1 s » (espace avant les deux-points en
// français), « Target: 1 s » en anglais.
const VIZ_LABELS = {
  fr: {
    total: 'Total',
    increase: 'Hausse',
    decrease: 'Baisse',
    cumulative: 'Cumul',
    value: 'Valeur',
    share: 'Part',
    target: 'Cible',
    start: 'Départ',
    end: 'Arrivée',
    series: (n) => `Série ${n}`,
    truncatedAxis: (unit) => `${unit || 'Valeur'} (axe tronqué)`,
    computed: (name, rule) => `${name} (calculé : ${rule})`,
    pair: (label, value) => `${label} : ${value}`,
  },
  en: {
    total: 'Total',
    increase: 'Increase',
    decrease: 'Decrease',
    cumulative: 'Cumulative',
    value: 'Value',
    share: 'Share',
    target: 'Target',
    start: 'Start',
    end: 'End',
    series: (n) => `Series ${n}`,
    truncatedAxis: (unit) => `${unit || 'Value'} (truncated axis)`,
    computed: (name, rule) => `${name} (calculated: ${rule})`,
    pair: (label, value) => `${label}: ${value}`,
  },
};

/** Textes fixes des graphiques dans la langue des textes dessinés (français par défaut). */
export const vizLabels = (lang) => VIZ_LABELS[vizLang(lang)];

/** Échappement HTML (infobulles ECharts en HTML : les libellés viennent du modèle). */
export function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}
